import {
    memo,
    Profiler,
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
    type ProfilerOnRenderCallback,
    type ReactNode,
} from "react";
import { CopyIcon, EditIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./TooltipPlayground.module.scss";

type TooltipImplementation = "none" | "new";

const implementations: ReadonlyArray<{
    value: TooltipImplementation;
    label: string;
}> = [
    { value: "none", label: "No tooltip" },
    { value: "new", label: "Tooltip" },
];

const ROW_COUNT = 500;
const rowNumbers = Array.from({ length: ROW_COUNT }, (_, index) => index + 1);

const RowTrigger = ({
    implementation,
    label,
    icon,
}: {
    implementation: TooltipImplementation;
    label: string;
    icon: ReactNode;
}) => {
    const button = (
        <Button variant="secondary" size="sm" iconOnly aria-label={label}>
            {icon}
        </Button>
    );
    if (implementation === "none") {
        return button;
    }
    return <Tooltip content={label}>{button}</Tooltip>;
};

const StressRows = memo(function StressRows({
    implementation,
    onMounted,
}: {
    implementation: TooltipImplementation;
    onMounted: () => void;
}) {
    useLayoutEffect(() => {
        onMounted();
    }, [onMounted]);

    return (
        <div className={styles.stressList}>
            {rowNumbers.map((rowNumber) => (
                <div key={rowNumber} className={styles.stressRow}>
                    <span className={styles.stressRowLabel}>
                        Row {rowNumber}
                    </span>
                    <RowTrigger
                        implementation={implementation}
                        label={`Edit row ${rowNumber}`}
                        icon={<EditIcon />}
                    />
                    <RowTrigger
                        implementation={implementation}
                        label={`Copy row ${rowNumber}`}
                        icon={<CopyIcon />}
                    />
                </div>
            ))}
        </div>
    );
});

/**
 * Dev-only performance check: 500 rows x 2 triggers, mounted with
 * `Tooltip` or with no tooltip as a baseline (results in the tooltip spec).
 * React Profiler reports the mount commit (dev builds only); the wall-clock
 * time from the click to the list's layout effect also works in production.
 */
export const TooltipStressSandbox = () => {
    const [implementation, setImplementation] =
        useState<TooltipImplementation>("new");
    const [mountCount, setMountCount] = useState(0);
    const [mountDurationMs, setMountDurationMs] = useState<number | null>(null);
    const [wallClockMs, setWallClockMs] = useState<number | null>(null);
    const startedAtRef = useRef<number | null>(null);
    const handleMounted = useCallback(() => {
        if (startedAtRef.current !== null) {
            setWallClockMs(performance.now() - startedAtRef.current);
            startedAtRef.current = null;
        }
    }, []);

    const handleRender: ProfilerOnRenderCallback = (
        id,
        phase,
        actualDuration,
    ) => {
        if (phase !== "mount") {
            return;
        }
        // eslint-disable-next-line no-console -- the sandbox's measurement readout
        console.info(`[${id}] mount commit ${actualDuration.toFixed(1)}ms`);
        setMountDurationMs(actualDuration);
    };

    return (
        <VStack className={styles.section} gap={16} padding={24}>
            <h2 className={styles.sectionTitle}>Stress sandbox</h2>
            <p className={styles.note}>
                {ROW_COUNT} rows x 2 triggers. Switching or remounting mounts
                the list again; compare 3 runs each.
            </p>
            <HStack gap={12} align="center" wrap>
                <ToggleGroup.Root<TooltipImplementation>
                    value={implementation}
                    onValueChange={(nextImplementation) => {
                        if (nextImplementation) {
                            startedAtRef.current = performance.now();
                            setMountDurationMs(null);
                            setImplementation(nextImplementation);
                        }
                    }}
                >
                    {implementations.map((option) => (
                        <ToggleGroup.Item
                            key={option.value}
                            value={option.value}
                        >
                            {option.label}
                        </ToggleGroup.Item>
                    ))}
                </ToggleGroup.Root>
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                        startedAtRef.current = performance.now();
                        setMountDurationMs(null);
                        setMountCount((count) => count + 1);
                    }}
                >
                    Remount
                </Button>
                <span className={styles.stressResult}>
                    {mountDurationMs === null
                        ? "Measuring..."
                        : `Mount commit: ${mountDurationMs.toFixed(1)}ms`}
                </span>
                <span className={styles.stressResult}>
                    {wallClockMs === null
                        ? ""
                        : `Click to layout: ${wallClockMs.toFixed(1)}ms`}
                </span>
            </HStack>
            <Profiler
                key={`${implementation}-${mountCount}`}
                id={`tooltip-stress-${implementation}`}
                onRender={handleRender}
            >
                <StressRows
                    implementation={implementation}
                    onMounted={handleMounted}
                />
            </Profiler>
        </VStack>
    );
};
